import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ARTICLES, BLOG_CATEGORIES, getFeatured } from "@/data/blog";
import { Seo } from "@/components/Seo";

function ArticleCard({ a }) {
  return (
    <Link
      to={`/blog/${a.slug}`}
      data-testid={`blog-card-${a.slug}`}
      className="group block rounded-2xl overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00B5D8]"
    >
      <div className="aspect-[4/3] overflow-hidden bg-[#111]">
        <img
          src={a.hero}
          alt={a.heroAlt}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <div className="pt-4">
        <p className="text-xs uppercase tracking-widest text-[#00B5D8]">{a.category} · {a.dateLabel}</p>
        <h3 className="mt-2 font-display text-xl leading-snug group-hover:text-white/90">{a.title}</h3>
        <p className="mt-2 text-sm text-white/60 line-clamp-2">{a.excerpt}</p>
        <span className="mt-3 inline-flex items-center gap-2 text-sm text-white group-hover:gap-3 transition-all">
          Read more <ArrowRight size={15} className="text-[#00B5D8]" />
        </span>
      </div>
    </Link>
  );
}

export default function Blog() {
  const featured = getFeatured();
  const [cat, setCat] = useState("All");

  const cats = useMemo(() => {
    const used = new Set(ARTICLES.map((a) => a.category));
    return BLOG_CATEGORIES.filter((c) => c === "All" || used.has(c));
  }, []);

  const rest = useMemo(
    () => ARTICLES.filter((a) => a.slug !== featured.slug && (cat === "All" || a.category === cat)),
    [cat, featured.slug]
  );

  const siteUrl = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div className="bg-black text-white min-h-screen" data-testid="blog-page">
      <Seo
        title="Blog | Preshh"
        description="Stories, ideas and news from Preshh and the world of modern beverage innovation."
        image={featured.hero.startsWith("http") ? featured.hero : `${siteUrl}${featured.hero}`}
        url={`${siteUrl}/blog`}
      />
      <div className="max-w-7xl mx-auto px-6 pt-28 pb-20">
        <header className="max-w-2xl">
          <h1 className="font-display text-5xl sm:text-6xl">Blog</h1>
          <p className="mt-4 text-base md:text-lg text-white/60">
            Stories, ideas and news from Preshh and the world of modern beverage innovation.
          </p>
        </header>

        {/* Featured story */}
        <Link
          to={`/blog/${featured.slug}`}
          data-testid="blog-featured"
          className="group mt-12 grid lg:grid-cols-2 gap-8 items-center rounded-3xl overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00B5D8]"
        >
          <div className="aspect-[16/10] overflow-hidden rounded-3xl bg-[#111]">
            <img src={featured.hero} alt={featured.heroAlt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-[#00B5D8]">Featured · {featured.category} · {featured.dateLabel}</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl leading-tight">{featured.title}</h2>
            <p className="mt-4 text-white/60">{featured.excerpt}</p>
            <span className="mt-5 inline-flex items-center gap-2 text-sm text-white group-hover:gap-3 transition-all">
              Read the story <ArrowRight size={16} className="text-[#00B5D8]" />
            </span>
          </div>
        </Link>

        {/* Category filter */}
        <div className="mt-16 flex flex-wrap gap-2" role="tablist" aria-label="Article categories">
          {cats.map((c) => (
            <button
              key={c}
              role="tab"
              aria-selected={cat === c}
              data-testid={`blog-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setCat(c)}
              className={`text-xs uppercase tracking-widest px-4 py-2 rounded-full border transition-colors ${
                cat === c ? "bg-[#00B5D8] text-black border-[#00B5D8]" : "border-white/15 text-white/70 hover:text-white hover:border-white/40"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid">
          {rest.length === 0 ? (
            <p className="text-white/50 col-span-full py-10">No stories in this category yet.</p>
          ) : (
            rest.map((a) => <ArticleCard key={a.slug} a={a} />)
          )}
        </div>
      </div>
    </div>
  );
}
